import clsx from 'clsx'
import { Children, Fragment, type PropsWithChildren } from 'react'

import {
  FvButton,
  type FvButtonProps,
  FvLinkButton,
  OrDivider,
} from '@fv/client-components'
import type { PropsWithClassName } from '@fv/client-types'

import { ActionList } from './ActionList'
import { ShipperTooltip } from './ShipperTooltip'

type Props = PropsWithChildren<
  PropsWithClassName<
    Pick<FvButtonProps, 'icon' | 'disabled' | 'transform'> & {
      isActive?: boolean
      to?: string
      onClick?: () => void
      iconClass?: string
      keepIconColorOnActive?: boolean
      loading?: boolean
      counter?: number
      tooltip?: string
      tooltipClassName?: string
    }
  >
>
export const TabLink = ({
  to,
  isActive,
  onClick,
  className,
  children,
  icon,
  iconClass,
  transform,
  keepIconColorOnActive,
  loading,
  disabled,
  counter,
  tooltip,
  tooltipClassName,
}: Props) => {
  const wrapperClassName = 'px-3 flex items-center gap-x-2 fa-width-auto'
  const activeUnderlineClassName =
    'underline decoration-2 decoration-fv-orange-bright underline-offset-8 pointer-events-none'
  const iconClassName = clsx(
    iconClass,
    isActive && !keepIconColorOnActive ? 'text-fv-gray-dark' : '',
  )

  if (!to) {
    return (
      <ShipperTooltip label={tooltip} classNames={{ popup: tooltipClassName }}>
        <div className={wrapperClassName}>
          {typeof counter === 'number' && (
            <span className="inline-block leading-none bg-fv-beer-light border-fv-beer-medium rounded-xs px-1 py-[.1rem] border-2 text-center">
              {counter}
            </span>
          )}
          <FvButton
            onClick={onClick}
            loading={loading}
            icon={icon}
            iconClass={iconClassName}
            transform={transform}
            className={clsx(
              className,
              '!h-auto !bg-transparent !px-0 !leading-none relative',
              {
                [activeUnderlineClassName]: isActive,
              },
            )}
            disabled={disabled}
          >
            {children}
          </FvButton>
        </div>
      </ShipperTooltip>
    )
  }

  return (
    <div className={wrapperClassName}>
      <FvLinkButton
        to={to}
        iconClass={iconClassName}
        icon={loading ? 'spinner' : icon}
        transform={transform}
        className={({ isActive: linkIsActive }) =>
          clsx('relative block leading-none first:pl-0', className, {
            [activeUnderlineClassName]:
              isActive === true || (linkIsActive && isActive !== false),
          })
        }
      >
        {children && <span>{children}</span>}
      </FvLinkButton>
    </div>
  )
}

type TabLinkListProps = PropsWithChildren<
  PropsWithClassName & {
    type?: 'or'
  }
>

export const TabLinkList = (props: TabLinkListProps) => {
  const { type, ...restProps } = props

  if (type === 'or') {
    const childArray = Children.toArray(props.children)
    return (
      <div
        className={clsx(
          'flex items-center [&>*:first-child]:pl-0 [&>*:last-child]:pr-0',
          props.className,
        )}
      >
        {childArray.map((child, index) => (
          <Fragment key={index}>
            {child}
            {index < childArray.length - 1 && <OrDivider />}
          </Fragment>
        ))}
      </div>
    )
  }

  return <ActionList {...restProps} />
}
